// Print a copy of the rendered post so Markdown, charts and rich text stay intact.
export async function printJournal({ content, title, date, tags = [] }) {
  if (!content) throw new Error('Journal preview is not ready.');
  const postTitle = String(title || '').trim() || 'Untitled journal entry';
  let previousTitle;
  const frame = document.createElement('iframe');
  frame.title = postTitle;
  frame.setAttribute('aria-hidden', 'true');
  Object.assign(frame.style, { position: 'fixed', left: '-10000px', top: '0', width: '800px', height: '1000px', border: '0' });
  document.body.appendChild(frame);
  const printDocument = frame.contentDocument;
  const cleanup = () => {
    if (previousTitle !== undefined) document.title = previousTitle;
    frame.remove();
  };
  try {
    const base = printDocument.createElement('base');
    base.href = document.baseURI;
    printDocument.head.appendChild(base);
    printDocument.title = postTitle;
    printDocument.documentElement.lang = document.documentElement.lang || 'en';
    const stylesReady = Array.from(document.querySelectorAll('link[rel="stylesheet"], style')).map((source) => {
      const copy = source.cloneNode(true);
      const ready = source.tagName === 'LINK' ? new Promise((resolve, reject) => {
        copy.onload = resolve;
        copy.onerror = () => reject(new Error('Could not load print formatting. Please try again.'));
      }) : Promise.resolve();
      printDocument.head.appendChild(copy);
      return ready;
    });
    const style = printDocument.createElement('style');
    style.textContent = `
      @page { size: auto; margin: 16mm; }
      html, body { background: white !important; color: #1e293b; height: auto !important; overflow: visible !important; }
      body { margin: 0; font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.5; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      .journal-print-heading { margin-bottom: 20px; border-bottom: 1px solid #cbd5e1; padding-bottom: 12px; }
      .journal-print-heading h1 { font-size: 22pt; line-height: 1.2; margin: 0 0 8px; overflow-wrap: anywhere; }
      .journal-print-heading p { margin: 4px 0; font-size: 10pt; color: #475569; }
      .forum-rich-content { font-size: 11pt; }
      h1, h2, h3, h4, summary { break-after: avoid-page; }
      p { orphans: 3; widows: 3; }
      img, svg { max-width: 100%; height: auto; }
      img, table, pre, blockquote, .journal-pocket-chart-row,
      .journal-pocket-timeline > li, .journal-pocket-decisions > li { break-inside: avoid; }
      pre { white-space: pre-wrap; overflow-wrap: anywhere; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #cbd5e1; padding: 6px; }
      thead { display: table-header-group; }
      .journal-pocket { break-inside: auto; }
      .journal-pocket-chart-row { grid-template-columns: minmax(0, 1fr) minmax(0, 1.5fr) 3.5rem; }
      .journal-pocket-chart-row > [role="rowheader"] { grid-column: auto; }
      [class*="max-h-"], [class*="overflow-"] { max-height: none !important; overflow: visible !important; }
    `;
    printDocument.head.appendChild(style);
    const heading = printDocument.createElement('header');
    heading.className = 'journal-print-heading';
    const headingTitle = printDocument.createElement('h1');
    headingTitle.textContent = postTitle;
    heading.appendChild(headingTitle);
    [date, tags.map((tag) => `#${tag}`).join(' ')].filter(Boolean).forEach((text) => {
      const paragraph = printDocument.createElement('p');
      paragraph.textContent = text;
      heading.appendChild(paragraph);
    });
    printDocument.body.appendChild(heading);
    const copy = content.cloneNode(true);
    copy.querySelectorAll('details').forEach((details) => { details.open = true; });
    copy.querySelectorAll('[contenteditable]').forEach((element) => element.removeAttribute('contenteditable'));
    copy.querySelectorAll('img').forEach((image) => { image.loading = 'eager'; });
    printDocument.body.appendChild(copy);
    const imagesReady = Array.from(copy.querySelectorAll('img')).map((image) => image.complete ? Promise.resolve() : new Promise((resolve) => {
      image.onload = resolve;
      image.onerror = resolve;
    }));
    let timeout;
    try {
      await Promise.race([
        Promise.all([...stylesReady, ...imagesReady]).then(() => printDocument.fonts.ready),
        new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error('Print formatting is taking too long to load. Please try again.')), 20000); })
      ]);
    } finally { clearTimeout(timeout); }
    await new Promise((resolve) => frame.contentWindow.requestAnimationFrame(() => frame.contentWindow.requestAnimationFrame(resolve)));
    frame.contentWindow.addEventListener('afterprint', cleanup, { once: true });
    // Some browsers use the parent page title for the suggested PDF filename.
    previousTitle = document.title;
    document.title = postTitle;
    frame.contentWindow.focus();
    frame.contentWindow.print();
  } catch (error) {
    cleanup();
    throw error;
  }
}
